<template>
  <div>
    <header>
      <h3 class="float-left">廣告審核</h3>
    </header>
    <v-container>
      <v-row>
        <v-col cols="12" sm="6">
          <v-select
            v-model="status"
            :items="statuses"
            label="狀態"
            outlined
            dense
            hide-details
            @change="load"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" class="text-sm-right">
          <v-btn color="primary" to="/dashboard/ads/report">總覽報表</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table :headers="headers" :items="ads" :loading="loading">
            <template v-slot:item.slot="{ item }">
              {{ slotText(item.slot) }}
            </template>
            <template v-slot:item.status="{ item }">
              {{ statusText(item.status) }}
            </template>
            <template v-slot:item.stats.ctr="{ item }">
              {{ formatCTR(item.stats && item.stats.ctr) }}
            </template>
            <template v-slot:item.image="{ item }">
              <v-img :src="item.imageDataUrl" contain max-width="120"></v-img>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small color="primary" @click="openReview(item)">
                審核
              </v-btn>
              <v-btn
                v-if="item.paymentUrl"
                class="ml-2"
                small
                :href="item.paymentUrl"
                target="_blank"
              >
                付款連結
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="dialog" max-width="520px">
      <v-card v-if="selected">
        <v-card-title>{{ selected.title }}</v-card-title>
        <v-card-text>
          <v-img
            :src="selected.imageDataUrl"
            contain
            max-height="260"
            class="mb-4"
          ></v-img>
          <div class="mb-2">版位：{{ slotText(selected.slot) }}</div>
          <div class="mb-2">
            連結：
            <a :href="selected.url" target="_blank">{{ selected.url }}</a>
          </div>
          <div class="mb-2">
            聯絡：{{ selected.contactName }} / {{ selected.contactEmail }} /
            {{ selected.contactPhone }}
          </div>
          <div class="mb-4 plan-hint">
            申請方案：{{ selected.requestedDays }} 天 / 建議價格 NT$
            {{ suggestedPrice(selected).toLocaleString() }}
          </div>
          <v-text-field
            v-model.number="review.price"
            label="核准價格"
            outlined
            dense
            prefix="NT$"
          ></v-text-field>
          <v-select
            v-model.number="review.days"
            :items="dayItems"
            label="投放天數"
            outlined
            dense
            @change="syncPrice"
          ></v-select>
          <v-textarea
            v-model="review.reviewNote"
            label="審核備註"
            outlined
            dense
            rows="3"
          ></v-textarea>
          <v-divider class="my-4"></v-divider>
          <v-select
            v-model="statusUpdate.status"
            :items="statusOptions"
            label="廣告狀態"
            outlined
            dense
          ></v-select>
          <v-text-field
            v-model.number="statusUpdate.weight"
            label="投放權重"
            hint="1-10，數字越大輪播出現頻率越高"
            persistent-hint
            outlined
            dense
            type="number"
            min="1"
            max="10"
          ></v-text-field>
          <v-textarea
            v-model="statusUpdate.statusNote"
            label="狀態備註"
            outlined
            dense
            rows="2"
          ></v-textarea>
          <v-divider class="my-4"></v-divider>
          <div class="plan-hint mb-3">成效報表</div>
          <v-row dense>
            <v-col cols="4">
              <div class="metric">
                <div class="metric-label">曝光</div>
                <div class="metric-value">
                  {{ report.totals.impressions.toLocaleString() }}
                </div>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="metric">
                <div class="metric-label">點擊</div>
                <div class="metric-value">
                  {{ report.totals.clicks.toLocaleString() }}
                </div>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="metric">
                <div class="metric-label">CTR</div>
                <div class="metric-value">{{ formatCTR(report.totals.ctr) }}</div>
              </div>
            </v-col>
          </v-row>
          <div class="mt-3 mb-2">
            投放期間：{{ selected.startsAt || "-" }} ~ {{ selected.endsAt || "-" }}
          </div>
          <div class="mb-3">剩餘天數：{{ report.remainingDaysText }}</div>
          <v-data-table
            dense
            :headers="reportHeaders"
            :items="report.daily"
            :items-per-page="5"
            class="report-table"
          >
            <template v-slot:item.ctr="{ item }">
              {{ formatCTR(item.ctr) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn color="grey darken-1" text @click="archive">封存</v-btn>
          <v-btn v-if="selected.status == 'submitted'" color="error" text @click="reject">
            退回並寄信
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">取消</v-btn>
          <v-btn color="secondary" @click="updateStatus">更新狀態</v-btn>
          <v-btn
            v-if="selected.status == 'submitted'"
            color="primary"
            @click="approve"
          >
            審核通過並寄信
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/libs/config.js";

export default {
  name: "AdsDashboard",
  data: () => ({
    loading: false,
    dialog: false,
    status: "submitted",
    selected: null,
    ads: [],
    report: {
      totals: { impressions: 0, clicks: 0, ctr: 0 },
      daily: [],
      remainingDaysText: "-",
    },
    review: {
      price: null,
      days: 7,
      reviewNote: "",
    },
    statusUpdate: {
      status: "",
      weight: 1,
      statusNote: "",
    },
    plans: {
      top: { 7: 400, 14: 800, 30: 1500 },
      floating: { 7: 400, 14: 800, 30: 1500 },
    },
    dayItems: [
      { text: "7 天", value: 7 },
      { text: "14 天", value: 14 },
      { text: "30 天", value: 30 },
    ],
    statuses: [
      { text: "待審核", value: "submitted" },
      { text: "待付款", value: "approved_pending_payment" },
      { text: "已寄付款信", value: "payment_notice_sent" },
      { text: "投放中", value: "active" },
      { text: "已退回", value: "rejected" },
      { text: "已停用", value: "disabled" },
      { text: "全部", value: "" },
    ],
    statusOptions: [
      { text: "待審核", value: "submitted" },
      { text: "待付款", value: "approved_pending_payment" },
      { text: "已寄付款信", value: "payment_notice_sent" },
      { text: "投放中", value: "active" },
      { text: "已退回", value: "rejected" },
      { text: "已停用", value: "disabled" },
    ],
    headers: [
      { text: "建立時間", value: "createdAt" },
      { text: "版位", value: "slot" },
      { text: "圖片", value: "image", sortable: false },
      { text: "標題", value: "title" },
      { text: "聯絡人", value: "contactName" },
      { text: "狀態", value: "status" },
      { text: "曝光", value: "stats.impressions" },
      { text: "點擊", value: "stats.clicks" },
      { text: "CTR", value: "stats.ctr" },
      { text: "操作", value: "actions", sortable: false },
    ],
    reportHeaders: [
      { text: "日期", value: "date" },
      { text: "曝光", value: "impressions" },
      { text: "點擊", value: "clicks" },
      { text: "CTR", value: "ctr" },
    ],
  }),
  created() {
    this.load();
  },
  methods: {
    slotText(slot) {
      return slot == "top" ? "首頁主要廣告區" : "浮動廣告區";
    },
    statusText(status) {
      return (
        this.statusOptions.find((item) => item.value == status)?.text || status
      );
    },
    formatCTR(value) {
      return `${((value || 0) * 100).toFixed(2)}%`;
    },
    suggestedPrice(item) {
      return this.plans[item.slot]?.[item.requestedDays || 7] || 0;
    },
    syncPrice() {
      this.review.price =
        this.plans[this.selected.slot]?.[this.review.days] || this.review.price;
    },
    async load() {
      this.loading = true;
      const { data } = await axios.get(`${config.API_URL}/ads/admin/applications`, {
        params: { status: this.status || undefined },
      });
      this.ads = data.ads;
      this.loading = false;
    },
    openReview(item) {
      this.selected = item;
      this.review = {
        price: item.price || this.suggestedPrice(item),
        days: item.days || item.requestedDays || 7,
        reviewNote: item.reviewNote || "",
      };
      this.statusUpdate = {
        status: item.status,
        weight: item.weight || 1,
        statusNote: item.statusNote || "",
      };
      this.syncPrice();
      this.dialog = true;
      this.loadReport(item._id);
    },
    async loadReport(id) {
      this.report = {
        totals: { impressions: 0, clicks: 0, ctr: 0 },
        daily: [],
        remainingDaysText: "-",
      };
      const { data } = await axios.get(`${config.API_URL}/ads/admin/reports/${id}`);
      this.report = {
        totals: data.totals || { impressions: 0, clicks: 0, ctr: 0 },
        daily: data.daily || [],
        remainingDaysText:
          data.remainingDays === null ? "-" : `${data.remainingDays} 天`,
      };
    },
    async approve() {
      await axios.post(
        `${config.API_URL}/ads/admin/applications/${this.selected._id}/review`,
        {
          action: "approve",
          ...this.review,
        },
      );
      this.$toast.success("已審核通過，並建立付款通知。");
      this.dialog = false;
      this.load();
    },
    async reject() {
      if (!this.review.reviewNote) {
        this.$toast.error("請先填寫退回原因。");
        return;
      }
      await axios.post(
        `${config.API_URL}/ads/admin/applications/${this.selected._id}/review`,
        {
          action: "reject",
          reviewNote: this.review.reviewNote,
        },
      );
      this.$toast.success("已退回，並建立退回通知。");
      this.dialog = false;
      this.load();
    },
    async updateStatus() {
      await axios.post(
        `${config.API_URL}/ads/admin/applications/${this.selected._id}/status`,
        this.statusUpdate,
      );
      this.$toast.success("狀態已更新。");
      this.dialog = false;
      this.load();
    },
    async archive() {
      if (!confirm("確認封存這筆廣告？封存後將不會出現在列表。")) {
        return;
      }
      await axios.post(
        `${config.API_URL}/ads/admin/applications/${this.selected._id}/archive`,
      );
      this.$toast.success("已封存。");
      this.dialog = false;
      this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
header {
  padding: 0 20px;
  height: 50px;
  line-height: 50px;
  background-color: #1976d2;
  color: #fff;
}
.plan-hint {
  color: #1976d2;
  font-weight: 700;
}
.metric {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}
.metric-label {
  color: #666;
  font-size: 13px;
}
.metric-value {
  color: #1976d2;
  font-size: 20px;
  font-weight: 700;
}
.report-table {
  border: 1px solid #eee;
}
</style>
